import React, { useEffect, useState } from "react";
// Removed unused firestore imports
import { SlidersHorizontal, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { fadeUp, staggerContainer } from "../lib/animations";
import { useTranslation } from "react-i18next";
import ProductCard from "../components/ui/ProductCard";
import { productService } from "../services/product.service";

const AllProductsPage = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // Filtre durumları
  const [category, setCategory] = useState("all");
  const [sortBy, setSortBy] = useState("none");
  const [priceRange, setPriceRange] = useState([0, 2000]);

  // Firestore ürünleri çek
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const allData = await productService.getAllProducts();
        // Filter only active products for public view
        const activeProducts = allData.filter(p => p.isActive);
        console.log(`[AllProducts] Received active products: ${activeProducts.length}`);
        setProducts(activeProducts);
        setFiltered(activeProducts);
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    fetchProducts();
  }, []);

  // Update category from URL on mount/update
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const catParam = params.get('category');
    if (catParam) {
      setCategory(catParam);
    }
  }, []);

  // Filtreleme sistemi
  useEffect(() => {
    let data = [...products];

    // Kategori
    if (category !== "all") {
      data = data.filter((p) => p.category === category);
    }

    // Fiyat aralığı
    data = data.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    // Sırala
    if (sortBy === "priceLow") data.sort((a, b) => a.price - b.price);
    if (sortBy === "priceHigh") data.sort((a, b) => b.price - a.price);
    if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));

    setFiltered(data);
  }, [category, sortBy, priceRange, products]);

  // Derive dynamic categories from loaded products
  const uniqueCategories = ["all", ...new Set(products.map(p => p.category).filter(Boolean))];

  return (
    <div className="bg-fecos-bg min-h-screen pb-24 pt-10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">

        {/* HEADER */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-fecos-text tracking-tight mb-4">
              {t("allProducts.title")}
            </h1>
            <p className="text-fecos-gray">{t("allProducts.subtitle")}</p>
          </div>
          <button className="lg:hidden p-2 rounded-full bg-white shadow-sm text-fecos-text">
            <SlidersHorizontal className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-12">

          {/* SOL FİLTRE PANELİ (Desktop) */}
          <div className="hidden lg:block w-64 shrink-0 space-y-8 sticky top-28 h-fit">

            {/* Kategoriler */}
            <div>
              <p className="text-xs font-bold text-fecos-text uppercase tracking-widest mb-4">
                {t("allProducts.filters.categories")}
              </p>
              <div className="space-y-2">
                {uniqueCategories.map((catValue) => (
                  <label key={catValue} className="flex items-center gap-3 cursor-pointer group">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${category === catValue ? 'border-fecos-accent bg-fecos-accent' : 'border-gray-300 group-hover:border-fecos-accent'}`}>
                      {category === catValue && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                    </div>
                    <input
                      type="radio"
                      className="hidden"
                      checked={category === catValue}
                      onChange={() => setCategory(catValue)}
                    />
                    <span className={`text-sm capitalize ${category === catValue ? 'text-fecos-text font-semibold' : 'text-fecos-gray group-hover:text-fecos-text'} transition-colors`}>{catValue === 'all' ? t("allProducts.filters.all") : catValue}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Fiyat Aralığı */}
            <div>
              <p className="text-xs font-bold text-fecos-text uppercase tracking-widest mb-4">
                {t("allProducts.filters.priceRange")}
              </p>
              <input
                type="range"
                min="0"
                max="2000"
                step="50"
                value={priceRange[1]}
                onChange={(e) => setPriceRange([0, Number(e.target.value)])}
                className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-fecos-accent"
              />
              <p className="text-xs mt-3 text-fecos-gray font-medium">
                0₺ – {priceRange[1]}₺
              </p>
            </div>

            {/* Sırala */}
            <div>
              <p className="text-xs font-bold text-fecos-text uppercase tracking-widest mb-4">
                {t("allProducts.filters.sortBy")}
              </p>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full appearance-none bg-white border border-fecos-border rounded-xl px-4 py-3 text-sm text-fecos-text outline-none focus:ring-1 focus:ring-fecos-accent/50 cursor-pointer"
                >
                  <option value="none">{t("allProducts.sort.featured")}</option>
                  <option value="priceLow">{t("allProducts.sort.priceLow")}</option>
                  <option value="priceHigh">{t("allProducts.sort.priceHigh")}</option>
                  <option value="az">{t("allProducts.sort.az")}</option>
                  <option value="za">{t("allProducts.sort.za")}</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* ÜRÜN GRİDİ */}
          <div className="flex-1">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-fecos-gray">
                <p>{t("allProducts.noProducts")}</p>
                <button onClick={() => { setCategory("all"); setSortBy("none"); setPriceRange([0, 2000]) }} className="mt-4 text-fecos-accent font-medium hover:underline">{t("allProducts.clearFilters")}</button>
              </div>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12"
              >
                {filtered.map((p) => (
                  <motion.div variants={fadeUp} key={p.id}>
                    <ProductCard product={p} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>

        </div>
      </div>
    </div >
  );
};

export default AllProductsPage;
